import { ListItem, Stack, Tooltip, Typography } from "@mui/material";
import { memo } from "react";
import { useDrag } from "react-dnd";
import { AreaItemType, MetaIcons } from "../../../../../../../../api/types/svTypes.ts";
import MetaDragListItemIcon from "../../../common/MetaDragListItemIcon.tsx";
import AddToChip from "../addTo/AddToChip.tsx";
import type { StatementChartAccount } from "../utils/statementChartOfAccounts.ts";
import { getAccountRowTooltip } from "../utils/statementChartOfAccounts.ts";

interface Props {
  account: StatementChartAccount;
  disabled: boolean;
  onDragStart: () => void;
}

const ChartOfAccountsRow = ({ account, disabled, onDragStart }: Props) => {
  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: AreaItemType.ACCOUNT,
      item: () => {
        onDragStart();
        return account;
      },
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
      canDrag: () => !disabled,
    }),
    [account, disabled, onDragStart]
  );

  return (
    <ListItem
      ref={(node) => {
        drag(node);
      }}
      sx={{
        height: 28,
        py: 0,
        px: 0.75,
        "& .meta-icon svg, & .drag-icon svg": { width: 18, height: 18 },
        "& .row-actions": { visibility: "hidden" },
        "&:hover": { cursor: "pointer", backgroundColor: "action.hover" },
        "&:hover .row-actions": { visibility: "visible" },
        opacity: isDragging || disabled ? 0.5 : 1,
      }}
    >
      <Stack direction="row" spacing={1} sx={{ minWidth: 0, width: "100%", alignItems: "center" }}>
        <Tooltip
          placement="right"
          title={<Typography variant="caption">{getAccountRowTooltip(account)}</Typography>}
          disableInteractive
          enterDelay={1000}
          enterNextDelay={1000}
        >
          <Stack direction="row" spacing={1} sx={{ minWidth: 0, flexGrow: 1, alignItems: "center" }}>
            <MetaDragListItemIcon icon={MetaIcons.Hash} sx={{ color: "secondary.light" }} />
            <Typography variant="caption" color="textSecondary" noWrap sx={{ flexShrink: 0, width: "2.75rem" }}>
              {account.accountNo}
            </Typography>
            <Typography variant="caption" noWrap sx={{ minWidth: 0 }}>
              {account.accountName}
            </Typography>
          </Stack>
        </Tooltip>
        {!disabled && <AddToChip source={{ kind: "account", account }} />}
      </Stack>
    </ListItem>
  );
};

export default memo(ChartOfAccountsRow);
